import { memo, useMemo, useState } from 'react'
import AceEditor from 'react-ace'
import { isNil, isObject, isString } from 'lodash-es'
import classNames from 'classnames'
import { useMemoizedFn } from 'ahooks'
import copy from 'copy-to-clipboard'
import { Button, message } from 'antd'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { IconFont } from '@/components'
import { LIMITED_ERROR_COMBO_NOT_ENOUGH } from '@/constants/pay'
import { LimitedAlertContent } from '@/features/pay/LimitedAlert'
import { useLogger } from '../context'
import { isEmptyString, isMarkdown } from '@/pages/flowPage/util'
import { FlowStatus } from '@/apis/flow'
import { StringContent, SwitchOutputDisplay } from '../../StringContent'
import { parseCardData } from '@/features/logger/utils/parse'
import { DEFAULT_OVERLAY_SCROLLBAR_OPTIONS } from '@/hooks/useScrollBar'

const ErrorOutput = memo(() => {
  const { output, switchTab } = useLogger()

  const handleGoOutput = useMemoizedFn(() => {
    switchTab('log')
  })

  return (
    <div className='min-h-66px flex flex-items-center b-rd-8px b-1 b-#ff5219 b-op-12 bg-#fff1ed px-16px py-8px'>
      <IconFont name='cuowu' className='text-16px m-r-8' />
      <div className='c-#e54a16 text-wrap line-height-20px'>
        {output?.errorMsg ===
        // 根据 errorMessage 判断是否要显示超期/耗尽的错误信息
        LIMITED_ERROR_COMBO_NOT_ENOUGH ? (
          <LimitedAlertContent prefix='该工作流所在的空间' />
        ) : (
          output?.errorMsg
        )}
      </div>
      {output?.errorStepName && (
        <Button
          className='p-0! ml-8px font-normal!'
          type='link'
          onClick={handleGoOutput}
        >
          查看日志
        </Button>
      )}
    </div>
  )
})

export const LoggerOutput = memo(() => {
  const { output, flowLog, logs } = useLogger()

  const result = output?.result || ''

  const isEmpty = useMemo(
    () => isNil(result) || isEmptyString(result),
    [result],
  )

  const isMarkdownType = useMemo(
    () => !isEmpty && isString(result) && isMarkdown(result),
    [result, isEmpty],
  )

  const isCodeType = useMemo(
    () => !isMarkdownType && !isString(result),
    [isMarkdownType, result],
  )

  const [showCard, setShowCard] = useState(true)

  const onCopy = () => {
    const text = isObject(result) ? JSON.stringify(result, null, 2) : result
    copy(text)
    message.success('复制成功')
  }

  const knowledgeBucket = useMemo(() => {
    if (!logs || !output || !output.result) return false
    const outNode = logs[logs.length - 2]

    // 这里根据 mimetype 判断是否是不合理，但上下文环境中只能获取到这个
    const cardList = parseCardData(output.result)
    if (cardList.some(each => !each.mimetype)) return false
    if (!outNode || outNode.input?.memoryType !== 'searchMemory') return false
    return outNode.input.memory
  }, [logs])

  if (flowLog?.run_status !== FlowStatus.SUCCEEDED) {
    return flowLog?.run_status && <ErrorOutput />
  }

  return (
    <div className='h-full py-20px border-1px rounded-8px'>
      <div
        className={classNames(
          'b-op-60 h-full relative w-full flex flex-col [&:hover_.output-button]:opacity-100',
        )}
      >
        <div className='flex items-center mb-12px h-24px flex-none px-20px'>
          <div className='text-14px text-font flex items-center text-#8D8D99 italic'>
            运行结果
          </div>

          <div className='ml-auto flex'>
            <Button
              type='text'
              size='small'
              className='output-button opacity-0 flex-center text-12px! text-#17171D ml-12px !px-4px ![&_.ant-btn-icon]:mr-4px'
              icon={<IconFont name='fuzhi2' className='text-14px' />}
              onClick={onCopy}
            >
              复制
            </Button>

            {isMarkdownType && knowledgeBucket && (
              <SwitchOutputDisplay
                className='ml-12px'
                value={showCard}
                onChange={setShowCard}
              />
            )}
          </div>
        </div>

        {isEmpty && (
          <div className='h-full flex-center c-bg_3 c-op-40 px-20px'>
            emmm, 流程貌似没有输出内容
          </div>
        )}

        {isMarkdownType && !isEmpty && (
          <OverlayScrollbarsComponent
            className='overflow-auto rounded-8px flex-1 px-20px'
            element='div'
            options={DEFAULT_OVERLAY_SCROLLBAR_OPTIONS}
            defer
          >
            <StringContent
              closeCopy
              content={result}
              bucketId={knowledgeBucket}
              showCard={showCard}
            />
          </OverlayScrollbarsComponent>
        )}

        {isCodeType && !isMarkdownType && (
          <div className='flex-auto rounded-8px px-20px'>
            <AceEditor
              value={JSON.stringify(result, null, 2)}
              theme='tomorrow'
              highlightActiveLine={false}
              readOnly
              height='100%'
              width='100%'
              mode='json'
              setOptions={{ wrap: true }}
            />
          </div>
        )}
      </div>
    </div>
  )
})
